import { ReactComponent as AiContextIcon } from 'module/ui/assets/svgs/ai-context.svg';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import {
  faPenToSquare,
  faTrash,
  faEraser,
  faEllipsis,
} from '@fortawesome/pro-regular-svg-icons';
import { faPlus as fsPlus } from '@fortawesome/pro-solid-svg-icons';
import { Textarea } from '@rocketlane/ui';
import { Button } from '@rocketlane/ui';
import { Dropdown } from 'antd';
import { AIContext } from 'api/document-agent/types';
import {
  useDeleteAdditionalAIContext,
  useDeleteQuestionAIContext,
  useUpdateAdditionalAIContexts,
  useUpdateQuestionAIContext,
} from 'queries/document-agent';
import React, { useState, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

interface AiContextPopoverProps {
  sectionId: string;
  nodeId?: string;
  isReadonly: boolean;
  documentAgentId?: string;
  initialContext?: AIContext;
  refetchContexts?: () => Promise<any>;
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: React.ReactNode;
  mode: 'override' | 'add';
}

const PopoverWrapper = styled.div`
  min-width: 270px;
  display: flex;
  flex-direction: row;
  gap: var(--spacing-03);
  margin-left: var(--spacing-07);
  border: 2px solid var(--orange-50);
  border-radius: var(--border-radius-small);
  background: white !important;
`;

const StyledTextArea = styled(Textarea)`
  padding: var(--spacing-03) !important;
  min-height: 76px !important;
  resize: vertical !important;
`;

const StyledDisplayText = styled(Textarea)`
  border: none !important;
  padding: var(--spacing-01) !important;
  min-height: 32px !important;
  outline: none !important;
  outline-color: transparent !important;
  outline-offset: 0 !important;
  width: 95% !important;
  background-color: transparent !important;

  &:focus,
  &:hover {
    border: none !important;
    outline: none !important;
    outline-color: transparent !important;
    outline-offset: 0 !important;
  }
`;

const CreateWrapper = styled.div`
  padding: var(--spacing-03);
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: var(--spacing-03);
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--spacing-03);
  justify-content: flex-end;
`;

const CardTitleWrapper = styled.div<{ isClickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  position: relative;
  flex-direction: row;
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
`;

const IconWrapper = styled.div<{ $isDefault?: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  z-index: 1;

  svg circle {
    fill: ${({ $isDefault }) => ($isDefault ? 'var(--gray-60)' : '')};
  }

  svg path {
    fill: ${({ $isDefault }) => ($isDefault ? 'var(--orange-50)' : '')};
  }
`;

const CardTitle = styled.div`
  color: var(--text-tertiary);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-02);
  font-weight: var(--font-weight-semibold);
  flex: 1;
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  padding-left: calc(var(--spacing-03) + 12px);
  margin-left: var(--spacing-04);
`;

export const AiContextPopover: React.FC<AiContextPopoverProps> = ({
  sectionId,
  nodeId,
  documentAgentId,
  initialContext,
  refetchContexts,
  onOpenChange,
  title,
  open,
  mode,
}) => {
  const [value, setValue] = useState(initialContext?.additionalContext || '');
  const [isOpen, setIsOpen] = useState(open);

  // Sync isOpen with open prop
  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const updateMutation = useUpdateAdditionalAIContexts({
    onSuccess: async () => {
      if (refetchContexts) {
        await refetchContexts?.();
      }
      setIsOpen(false);
      onOpenChange?.(false);
    },
  });

  const updateQuestionMutation = useUpdateQuestionAIContext({
    onSuccess: async () => {
      if (refetchContexts) {
        await refetchContexts?.();
      }
      setIsOpen(false);
      onOpenChange?.(false);
    },
  });

  const onCancel = () => {
    setIsOpen(false);
    onOpenChange?.(false);
    setValue(initialContext?.additionalContext || '');
  };

  useEffect(() => {
    // Update value when initialContext changes
    setValue(initialContext?.additionalContext || '');
  }, [initialContext]);

  const deleteMutation = useDeleteAdditionalAIContext({
    onSuccess: async () => {
      setIsOpen(false);
      await refetchContexts?.();
      onOpenChange?.(false);
    },
  });

  const deleteQuestionMutation = useDeleteQuestionAIContext({
    onSuccess: async () => {
      setIsOpen(false);
      await refetchContexts?.();
      onOpenChange?.(false);
    },
  });

  const handleDeleteContext = useCallback(() => {
    if (!documentAgentId || !sectionId) {
      return;
    }

    if (nodeId) {
      // Delete question-level context
      deleteQuestionMutation.mutate({
        documentAgentId: documentAgentId.toString(),
        templateSectionId: sectionId,
        nodeId,
      });
    } else {
      // Delete section-level context
      deleteMutation.mutate({
        documentAgentId: documentAgentId.toString(),
        templateSectionId: sectionId,
      });
    }
  }, [
    documentAgentId,
    sectionId,
    nodeId,
    deleteMutation,
    deleteQuestionMutation,
  ]);

  const handleSave = async () => {
    if (!documentAgentId || !value.trim()) {
      return;
    }

    if (nodeId) {
      // Question-level context - payload keyed by nodeId
      const payload = {
        [nodeId]: {
          additionalContext: value,
          override: mode === 'override',
        },
      };

      await updateQuestionMutation.mutate({
        documentAgentId,
        templateSectionId: sectionId,
        nodeId,
        payload,
      });
    } else {
      // Section-level context - payload keyed by sectionId
      const payload = {
        [sectionId]: {
          additionalContext: value,
          override: mode === 'override',
          templateSection: {
            title: initialContext?.templateSection?.title || 'Section Context',
            templateId: initialContext?.templateSection?.templateId || 0,
          },
        },
      };

      await updateMutation.mutate({ documentAgentId, payload });
    }
  };

  const moreActionsMenu = {
    items: [
      {
        key: 'edit',
        label: 'Edit',
        icon: <FontAwesomeIcon icon={faPenToSquare} />,
        onClick: () => {
          setIsOpen(true);
        },
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: <FontAwesomeIcon icon={faTrash} />,
        danger: true,
        onClick: () => {
          handleDeleteContext();
        },
      },
    ],
  };

  const isLoading =
    updateMutation.isPending ||
    updateQuestionMutation.isPending ||
    deleteMutation.isPending ||
    deleteQuestionMutation.isPending;

  return (
    <>
      {!isOpen ? (
        <>
          {value === '' ? (
            <CardTitleWrapper
              isClickable={true}
              onClick={() => {
                setIsOpen(true);
                onOpenChange?.(true);
              }}
            >
              <IconWrapper>
                <FontAwesomeIcon
                  icon={mode === 'add' ? fsPlus : faEraser}
                  size="sm"
                />
              </IconWrapper>
              <CardTitle>{title}</CardTitle>
            </CardTitleWrapper>
          ) : (
            <>
              <CardTitleWrapper>
                <IconWrapper $isDefault={true}>
                  <AiContextIcon width={16} height={16} />
                </IconWrapper>
                <CardTitle>
                  <FormattedMessage
                    id={`document_agent.preview.${mode?.toString()?.toLowerCase()}_done`}
                  />
                </CardTitle>
                <Dropdown
                  menu={moreActionsMenu}
                  placement="bottomLeft"
                  trigger={['click']}
                >
                  <Button
                    variant="ghost"
                    size="small"
                    leftIcon={<FontAwesomeIcon icon={faEllipsis} />}
                  />
                </Dropdown>
              </CardTitleWrapper>
              <ContentArea>
                <StyledDisplayText
                  value={value}
                  readOnly
                  rows={1}
                  maxRows={10}
                />
              </ContentArea>
            </>
          )}
        </>
      ) : (
        <CardTitleWrapper
          isClickable={true}
          onClick={() => {
            setIsOpen(true);
          }}
        >
          <IconWrapper>
            <FontAwesomeIcon
              icon={mode === 'add' ? fsPlus : faEraser}
              size="sm"
            />
          </IconWrapper>
          <CardTitle>{title}</CardTitle>
        </CardTitleWrapper>
      )}
      {isOpen && (
        <PopoverWrapper>
          <CreateWrapper>
            <StyledTextArea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Add more AI context"
              rows={1}
              maxRows={7}
              disabled={isLoading}
            />
            <ButtonContainer>
              <Button
                variant="secondary"
                onClick={onCancel}
                disabled={isLoading}
              >
                <FormattedMessage id="cancel" />
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={isLoading || !value.trim()}
                loading={isLoading}
              >
                <FormattedMessage id="save" />
              </Button>
            </ButtonContainer>
          </CreateWrapper>
        </PopoverWrapper>
      )}
    </>
  );
};
